import { useTranslation } from 'react-i18next'

import { Link } from 'react-router-dom'

import { ChevronRight } from 'lucide-react'

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar'

import type { NavItem } from '@/types/nav-item'

interface NavMainProps {
  items: NavItem[]
}

function groupNavItems(items: NavItem[]): Record<string, NavItem[]> {
  return items.reduce(
    (groups, item) => {
      const groupName = item?.group || 'Other'
      if (!groups[groupName]) groups[groupName] = []
      groups[groupName].push(item)
      return groups
    },
    {} as Record<string, NavItem[]>
  )
}

export function NavMain({ items }: NavMainProps) {
  const { t } = useTranslation(['dashboard'])
  const grouped = groupNavItems(items)

  const getGroupLabel = (groupName: string): string => {
    const groupTranslations: Record<string, string> = {
      Main: t('dashboard:nav.groups.main', 'Main'),
      Management: t('dashboard:nav.groups.management', 'Management'),
      Classes: t('dashboard:nav.groups.classes', 'Classes'),
      Health: t('dashboard:nav.groups.health', 'Health'),
      Notes: t('dashboard:nav.groups.notes', 'Notes'),
      Finance: t('dashboard:nav.groups.finance', 'Finance'),
      Reports: t('dashboard:nav.groups.reports', 'Reports'),
      Administration: t('dashboard:nav.groups.administration', 'Administration'),
      Settings: t('dashboard:nav.groups.settings', 'Settings'),
      Other: t('dashboard:nav.groups.other', 'Other'),
    }
    return groupTranslations[groupName] || groupName
  }

  return (
    <>
      {Object.entries(grouped).map(([group, groupItems]) => (
        <SidebarGroup key={group}>
          <SidebarGroupLabel>{getGroupLabel(group)}</SidebarGroupLabel>
          <SidebarMenu>
            {groupItems.map((item) =>
              item.items?.length ? (
                <Collapsible key={item.title} asChild className='group/collapsible'>
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton tooltip={item.title}>
                        {item.icon && <item.icon />}
                        <span>{item.title}</span>
                        <ChevronRight className='ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90' />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {item.items.map((subItem) => (
                          <SidebarMenuSubItem key={subItem.url}>
                            <SidebarMenuSubButton asChild>
                              <Link to={subItem.url}>{subItem.title}</Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              ) : (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton asChild tooltip={item.title}>
                    <Link to={item.url}>
                      {item.icon && <item.icon />}
                      {item.title}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )
            )}
          </SidebarMenu>
        </SidebarGroup>
      ))}
    </>
  )
}
